import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { makeGetSortedProjectAutomations } from 'selectors/automations';
import { makeGetFetchedState, makeGetLoadingState } from 'selectors/global';

import { fetchAutomations, updateAutomation } from 'actions/automations';
import { showModal } from 'actions/modal';

import config from 'config/index';

import { prepareAutomationForEdit } from 'utils/automation';

import Popout from 'components/popout';
import Toggle from 'components/toggle';
import Tooltip from 'components/tooltip';

import IconBolt from 'icons/icon-bolt';
import IconEdit from 'icons/icon-edit';

import './AutomationToggle.styles.css';

const AutomationItem = ({ id, name, disabled, onEdit }) => {
    const { t } = useTranslation('common');

    const handleEditClick = useCallback(() => {
        onEdit(id);
    }, [onEdit, id]);

    const handleToggle = useCallback(() => {
        updateAutomation(id, { disabled: !disabled });
    }, [id, disabled]);

    return (
        <div className="automation-item popout-row">
            <div className="item-main">
                <div className="item-title">
                    { name }
                </div>

                <Tooltip placement="top" withTooltipHover={false} tooltip={ t('Edit') }>
                    <button className="btn btn-xxsmall btn-icon btn-square btn-secondary btn-border item-edit" onClick={handleEditClick}>
                        <IconEdit size="12px" />
                    </button>
                </Tooltip>
            </div>

            <Tooltip placement="top" withTooltipHover={false} tooltip={disabled ? t('Enable') : t('Disable')}>
                <div className="item-foot">
                    <Toggle checked={!disabled} onChange={handleToggle} />
                </div>
            </Tooltip>
        </div>
    );
}

const AutomationToggle = ({ projectId, automations, loading, fetched, children }) => {
    const [popoutVisible, setPopoutVisible] = useState(false);
    const popoutCtrl = useRef(null);
    const { t } = useTranslation(['automation']);

    const handleToggleClick = useCallback(() => {
        showModal('EDIT_AUTOMATION', { projectId });
        // popoutCtrl.current?.show();
    }, [popoutCtrl]);

    const handlePopoutShow = useCallback(() => {
        setPopoutVisible(true);
    }, [setPopoutVisible]);

    const handlePopoutHide = useCallback(() => {
        setPopoutVisible(false);
    }, [setPopoutVisible]);

    const handleCreateClick = () => {
        showModal('EDIT_AUTOMATION', { projectId });
        popoutCtrl.current?.hide();
    }

    const handleAutomationEdit = useCallback(automationId => {
        const automation = automations.find(({ id }) => id === automationId);

        showModal('EDIT_AUTOMATION', {
            projectId,
            automationId,
            automation: prepareAutomationForEdit(automation)
        });

        popoutCtrl.current?.hide();
    }, [projectId, automations, popoutCtrl]);

    useEffect(() => {
        if (config.AUTOMATIONS_ENABLED && !loading && !fetched && projectId) {
            fetchAutomations(projectId);
        }
    }, [projectId, loading, fetched]);

    if (!config.AUTOMATIONS_ENABLED) {
        return null;
    }

    return (
        <div className="automation-toggle">
            <Tooltip
                placement="top"
                withTooltipHover={false}
                preventHideOnClick={false}
                tooltip={ t('Add Automations') }
            >
                {
                    children ? (
                        React.cloneElement(children, {
                            onClick: handleToggleClick
                        })
                    ) : (
                        <button className={'btn btn-medium btn-icon btn-square btn-secondary btn-border' + (popoutVisible ? ' selected' : '')} onClick={handleToggleClick}>
                            <IconBolt />
                        </button>
                    )
                }
            </Tooltip>

            <Popout
                className="no-arrow"
                xAlign="left"
                defaultXPosition="right"
                provideController={popoutCtrl}
                onShow={handlePopoutShow}
                onHide={handlePopoutHide}
            >
                <div className="popout-head">
                    { t('Project Automations') }
                </div>

                <div className="popout-main">
                    <div className="popout-body automations-list">
                        {
                            !automations.length && fetched ? (
                                <React.Fragment>
                                    { t('noEntries')}
                                </React.Fragment>
                            ) : null
                        }

                        {
                            automations.map(automation => <AutomationItem
                                key={automation.id}
                                id={automation.id}
                                name={automation.name}
                                disabled={automation.disabled}
                                onEdit={handleAutomationEdit}
                            />)
                        }
                    </div>
                </div>

                <div className="popout-foot">
                    <button className="btn btn-block btn-small btn-success" onClick={handleCreateClick}>+ { t('Add Automations') }</button>
                </div>
            </Popout>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getSortedProjectAutomations = makeGetSortedProjectAutomations();
    const getAutomationsFetched = makeGetFetchedState();
    const getAutomationsLoading = makeGetLoadingState();

    return ({ automations }, ownProps) => ({
        automations: getSortedProjectAutomations(automations.items, ownProps.projectId),
        loading: getAutomationsLoading(automations.fetchInfo, 'project', ownProps.projectId),
        fetched: getAutomationsFetched(automations.fetchInfo, 'project', ownProps.projectId)
    });
};

export default connect(makeMapStateToProps)(AutomationToggle);
